/**
 * Accounts, sessions and permissions.
 *
 * A `User` is a login. An `Employee` is a payroll record. They are separate
 * tables that may point at each other: the owner has a login and a salary, a
 * casual baker may have a payroll record and never sign in, and a stand-in
 * cashier may have a login with no payroll record at all.
 */
export const ALL_PERMISSIONS = [
    'pos.sell', 'pos.discount', 'pos.refund', 'pos.void',
    'orders.view', 'orders.edit', 'orders.change_status',
    'bakesheet.view', 'delivery.view', 'expenses.view',
    'customers.view', 'customers.edit',
    'inbox.view', 'inbox.reply',
    'inventory.view', 'inventory.edit', 'inventory.purchase',
    'products.view', 'products.edit',
    'reports.revenue', 'reports.margin',
    'staff.view', 'staff.edit', 'payroll.own', 'payroll.all',
    'users.manage', 'settings.edit',
];
export const PERMISSION_GROUPS = [
    {
        label: 'Register',
        permissions: [
            { key: 'pos.sell', label: 'Ring up sales', description: 'Use the register and take payment.' },
            { key: 'pos.discount', label: 'Apply discounts', description: 'Including senior and PWD discounts.' },
            { key: 'pos.refund', label: 'Refund', description: 'Return money against a completed sale.' },
            { key: 'pos.void', label: 'Void a sale', description: 'Cancel a sale and return the stock.' },
        ],
    },
    {
        label: 'Orders',
        permissions: [
            { key: 'orders.view', label: 'See orders', description: 'View the orders board.' },
            { key: 'orders.edit', label: 'Edit orders', description: 'Change items, dates and delivery details.' },
            { key: 'orders.change_status', label: 'Move orders along', description: 'Change status and send the customer message.' },
        ],
    },
    {
        label: "The day's sheets",
        permissions: [
            { key: 'bakesheet.view', label: 'Bake sheet', description: 'What to bake today, the shelf count and the kitchen total.' },
            { key: 'delivery.view', label: 'Delivery route', description: "The rider's round, with addresses and what to collect." },
        ],
    },
    {
        label: 'Clients and inbox',
        permissions: [
            { key: 'customers.view', label: 'See clients', description: 'Names, contact details, order history.' },
            { key: 'customers.edit', label: 'Edit clients', description: 'Add and change client records.' },
            { key: 'inbox.view', label: 'See conversations', description: 'Read the messaging inbox.' },
            { key: 'inbox.reply', label: 'Reply to customers', description: 'Send messages and create orders from chats.' },
        ],
    },
    {
        label: 'Stock and products',
        permissions: [
            { key: 'inventory.view', label: 'See the stock room', description: 'Ingredient levels and low-stock alerts.' },
            { key: 'inventory.edit', label: 'Adjust stock', description: 'Stock counts, waste and adjustments.' },
            { key: 'inventory.purchase', label: 'Receive deliveries', description: 'Record supplies and their prices.' },
            { key: 'products.view', label: 'See products', description: 'The menu and its prices.' },
            { key: 'products.edit', label: 'Edit products and recipes', description: 'Change prices, items and recipes.' },
        ],
    },
    {
        label: 'Money',
        permissions: [
            {
                key: 'reports.revenue',
                label: 'See sales totals',
                description: 'Takings by day, week and month, and the dashboard. Without this, only the order in hand is visible.',
            },
            {
                key: 'reports.margin',
                label: 'See costs and margin',
                description: 'What ingredients cost, profit per item, margin percentages, and the daily report of what each day really left.',
            },
            {
                key: 'expenses.view',
                label: 'Expenses',
                description: 'Rent, bills and the wage bill. Separate from takings, because what the shop spends is its own matter.',
            },
        ],
    },
    {
        label: 'Team',
        permissions: [
            { key: 'staff.view', label: 'See the team', description: 'Staff list and the roster.' },
            { key: 'staff.edit', label: 'Edit the team', description: 'Add and change staff records and rates.' },
            { key: 'payroll.own', label: 'See own payslip', description: 'Their own payslips only.' },
            { key: 'payroll.all', label: "See everyone's payslips", description: 'View and issue payslips for all staff.' },
        ],
    },
    {
        label: 'Administration',
        permissions: [
            { key: 'users.manage', label: 'Manage users', description: 'Add, edit and remove logins, and grant permissions.' },
            { key: 'settings.edit', label: 'Change settings', description: 'Shop details, order statuses, messages and channels.' },
        ],
    },
];
/**
 * What a plain staff account can do out of the box.
 *
 * Deliberately excludes `reports.revenue`, `reports.margin` and `payroll.all`:
 * a counter user can sell, take orders and talk to customers without seeing the
 * shop's takings, its cost base, or anyone else's pay.
 */
export const DEFAULT_STAFF_PERMISSIONS = [
    'pos.sell',
    'orders.view',
    'orders.edit',
    'orders.change_status',
    'bakesheet.view',
    'delivery.view',
    'customers.view',
    'customers.edit',
    'inbox.view',
    'inbox.reply',
    'inventory.view',
    'products.view',
    'staff.view',
    'payroll.own',
];
/** Admins hold everything; everyone else holds exactly what they were granted. */
export function effectivePermissions(user) {
    return user.role === 'admin' ? [...ALL_PERMISSIONS] : [...user.permissions];
}
export function can(user, permission) {
    if (!user)
        return false;
    if (user.role === 'admin')
        return true;
    return user.permissions.includes(permission);
}
export function checkPassword(password, forbid = []) {
    if (password.length < 8) {
        return { ok: false, message: 'Use at least 8 characters.' };
    }
    if (!/[a-zA-Z]/.test(password) || !/\d/.test(password)) {
        return { ok: false, message: 'Include at least one letter and one number.' };
    }
    // Blocks re-using the shipped starter passwords and the account's own handles.
    const lowered = password.toLowerCase();
    if (['admin', 'user', 'password', '12345678'].includes(lowered)) {
        return { ok: false, message: 'That password is too easy to guess.' };
    }
    if (forbid.some((value) => value && lowered === value.toLowerCase())) {
        return { ok: false, message: 'Do not use your name or email as the password.' };
    }
    return { ok: true, message: null };
}
//# sourceMappingURL=auth.js.map